import type { Id } from "@repo/convex/dataModel";
import { Link, useRouter } from "@tanstack/react-router";
import { ArrowLeft, ArrowLeftRight, Building2, FolderX, Home, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { StatusPanel } from "@/components/ui/status-panel";
import { useOrganization } from "@/contexts/organization-context";
import { useLocateProjectBySlug } from "../hooks";

interface ProjectNotFoundProps {
  /** The slug that was attempted */
  slug: string;
}

interface ProjectMatch {
  orgId: Id<"orgs">;
  orgName: string;
  projectId: Id<"projects">;
  projectName: string;
}

/** Value of `data-variant` on the page root — the E2E and unit hooks read it. */
type ProjectNotFoundVariant = "loading" | "accessible-elsewhere" | "no-access" | "not-found";

function NotFoundShell({
  variant,
  children,
}: {
  variant: ProjectNotFoundVariant;
  children: ReactNode;
}) {
  return (
    <main className="flex-1" data-testid="project-not-found-page" data-variant={variant}>
      {children}
    </main>
  );
}

/** Secondary when another action owns the primary slot (the org switch), primary otherwise. */
function AllProjectsLink({ variant = "default" }: { variant?: "default" | "outline" }) {
  return (
    <Link to="/" className={buttonVariants({ variant, size: "lg" })}>
      <Home data-icon="inline-start" />
      All Projects
    </Link>
  );
}

function GoBackButton() {
  const router = useRouter();

  return (
    <Button variant="outline" size="lg" onClick={() => router.history.back()}>
      <ArrowLeft data-icon="inline-start" />
      Go Back
    </Button>
  );
}

/**
 * Neutral placeholder while `locateBySlug` resolves. Without it the page would flash
 * "Project not found" before we know the project simply lives in another organization.
 */
function CheckingProject() {
  return (
    <NotFoundShell variant="loading">
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <p className="flex items-center gap-2 text-muted-foreground">
          <Spinner />
          Checking project…
        </p>
      </div>
    </NotFoundShell>
  );
}

/** The slug resolves in another organization the user belongs to — offer the switch. */
function ProjectInAnotherOrganization({
  slug,
  matches,
  onSwitch,
}: {
  slug: string;
  matches: ProjectMatch[];
  onSwitch: (orgId: Id<"orgs">) => void;
}) {
  const singleMatch = matches.length === 1 ? matches[0] : undefined;

  return (
    <NotFoundShell variant="accessible-elsewhere">
      <StatusPanel
        icon={Building2}
        title="This project is in another organization"
        description={
          singleMatch
            ? `"${singleMatch.projectName}" belongs to ${singleMatch.orgName}. Switching organizations closes your open tabs and opens this project.`
            : `"${slug}" exists in ${matches.length} of your organizations. Switching closes your open tabs.`
        }
        tone="info"
        actions={
          <>
            {matches.map((match) => (
              <Button
                key={match.orgId}
                size="lg"
                data-testid="project-not-found-switch-org"
                data-org-id={match.orgId}
                onClick={() => onSwitch(match.orgId)}
              >
                <ArrowLeftRight data-icon="inline-start" />
                Switch to {match.orgName}
              </Button>
            ))}
            <AllProjectsLink variant="outline" />
          </>
        }
      />
    </NotFoundShell>
  );
}

/** The slug exists, but only in organizations the user is not a member of. */
function ProjectInInaccessibleOrganization({ slug }: { slug: string }) {
  return (
    <NotFoundShell variant="no-access">
      <StatusPanel
        icon={Lock}
        title="You don't have access to this project"
        description={`The project "${slug}" belongs to an organization you're not a member of. Ask an administrator of that organization to invite you.`}
        tone="warning"
        actions={
          <>
            <GoBackButton />
            <AllProjectsLink />
          </>
        }
      />
    </NotFoundShell>
  );
}

/** No project anywhere carries this slug. */
function ProjectMissing({ slug }: { slug: string }) {
  return (
    <NotFoundShell variant="not-found">
      <StatusPanel
        icon={FolderX}
        title="Project not found"
        description={`The project "${slug}" doesn't exist, may have been deleted, or you don't have access.`}
        tone="warning"
        actions={
          <>
            <GoBackButton />
            <AllProjectsLink />
          </>
        }
      />
    </NotFoundShell>
  );
}

/**
 * Shown when `/project/:slug` doesn't resolve in the *active* organization.
 *
 * A project URL carries no organization, so "not in the active org" is not the same thing as
 * "does not exist" (LAT-627). `projects.locateBySlug` answers which of the caller's own
 * organizations owns the slug, and the page renders one of four states accordingly:
 *
 * - `loading` — the locate query is in flight; neutral copy, no verdict yet.
 * - `accessible-elsewhere` — the slug resolves in another organization the user belongs to;
 *   "Switch to {org}" switches with `keepCurrentLocation`, so the current deep link is
 *   re-opened in a fresh tab instead of dropping the user on the projects home.
 * - `no-access` — the slug exists, but only where the user is not a member. The server sends
 *   no organization or project identity for this case, so neither does the copy.
 * - `not-found` — nothing anywhere carries this slug (the original copy, asserted by
 *   `e2e/specs/error-pages.spec.ts`).
 *
 * Renders inline within the app shell (header still visible).
 */
export function ProjectNotFound({ slug }: ProjectNotFoundProps) {
  const { organizationId, switchOrganization } = useOrganization();
  const locate = useLocateProjectBySlug(slug);

  if (locate === undefined) {
    return <CheckingProject />;
  }

  // The active organization is excluded: if the slug resolved there, ProjectLayout would
  // never have rendered this page, so a match on it is stale data, not an offer to switch.
  const otherOrgMatches = locate.accessible.filter((match) => match.orgId !== organizationId);

  if (otherOrgMatches.length > 0) {
    return (
      <ProjectInAnotherOrganization
        slug={slug}
        matches={otherOrgMatches}
        onSwitch={(orgId) => switchOrganization(orgId, { keepCurrentLocation: true })}
      />
    );
  }

  if (locate.existsElsewhere) {
    return <ProjectInInaccessibleOrganization slug={slug} />;
  }

  return <ProjectMissing slug={slug} />;
}
